import Col from 'react-bootstrap/Col';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';

import ButtonDisclosures from '../../../atoms/button-disclosures';
import CardModule from '../../shared/card-module';
import TableBasic from '../../../atoms/table-basic/index';
import format from '../../../../utils/format';
import styles from './income-payout-rates.module.scss';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import moment from 'moment';

const className = augmentClassName(styles);

const IncomePayoutRates = ({ asOfDate, incomePayoutRates, isTIAAQdia }) => {
  const fundType = isInitial => {
    if (isTIAAQdia) {
      return `${isInitial && isTIAAQdia ? 'The' : 'the'} TIAA Secure Income Account`;
    }
    return 'TIAA Traditional';
  };

  const asOfDateFormated = format.date(asOfDate);
  const endMonthOfasOfDateFormated = moment(asOfDate).endOf('month').format('MM/DD/YYYY');
  const currentYear = moment(asOfDate).year();

  return (
    <CardModule
      data-analytics-componenttype="full-width-component"
      data-analytics-id="FW_IncomePayoutRates"
      data-analytics-section="component"
    >
      <Row>
        <Col md={4}>
          <CardModule.Header>Income Payout Rates</CardModule.Header>
          <CardModule.Body>
            <>
              {isTIAAQdia ? (
                <>
                  <p {...className('paragraph-emphasized')}>
                    An income payout rate represents the amount of annual income you will receive as
                    a percentage of the amount you convert to lifetime annuity payments.
                  </p>
                  <p {...className('paragraph-standard')}>
                    {`${fundType(
                      true
                    )} is designed primarily to help meet your long-term retirement income needs; it is not a short-term savings vehicle.`}
                  </p>
                  <p {...className('paragraph-standard')}>
                    {`When you are ready, you can choose when and how much to convert to lifetime income so you can be certain you (and a spouse or partner you may choose to include) will have income you cannot outlive - a choice that only an annuity can provide. If you can cover your basic living expenses with ${fundType(
                      false
                    )}, you will be able to use the other money you have saved for discretionary purposes.`}
                  </p>
                </>
              ) : (
                <>
                  <p {...className('paragraph-emphasized')}>
                    The actual total payout rate from TIAA Traditional depends on when you made your
                    contributions.
                  </p>
                  <p {...className('paragraph-standard')}>
                    {`When you are ready, you can choose when and how much to convert to lifetime income so you can be certain you (and a spouse or partner you may choose to include) will have income you cannot outlive - a choice that only an annuity can provide. If you can cover your basic living expenses with ${fundType(
                      false
                    )}, you will be able to use the other money you have saved for discretionary purposes.`}
                  </p>
                  <p {...className('paragraph-standard')}>
                    {`Because of our nonprofit heritage, TIAA seeks to share our profits with participants. Historically, TIAA has paid income above the guaranteed amount in two ways:`}
                  </p>
                  <p {...className('paragraph-standard')}>
                    {`Higher payout rates -- Long-term contributors can receive more lifetime income compared to someone who transfers the same amount at time of retirement. TIAA has paid more than the guaranteed minimum every year since 1949.`}
                  </p>
                  <p {...className('paragraph-standard')}>
                    {`Raises in retirement -- TIAA can increase your income payment amounts during your retirement. We have increased payments 19 times in the past 30 years.`}
                  </p>
                </>
              )}
              <p
                {...className('paragraph-standard', 'font-italic')}
              >{`Please see "more information" link for important disclosures.`}</p>
            </>
          </CardModule.Body>
        </Col>

        <Col md={8} {...className('d-flex', 'flex-column')}>
          <CardModule.Header {...className('font-size-14', 'bg-transparent')}>
            {'Income Payout Rates'}
            <br />
            <span {...className('as-of-date', 'from-to-label')}>
              {asOfDateFormated} - {endMonthOfasOfDateFormated} Once annuitized, total rate is
              guaranteed through December 31, {currentYear}.
            </span>
          </CardModule.Header>

          <CardModule.Body>
            <TableBasic>
              <caption {...className('sr-only')}>{`Income Payout Rates`}</caption>
              <thead>
                <tr>
                  <th {...className('border-0')}></th>
                  <th
                    colSpan="2"
                    {...className(
                      'text-center',
                      'text-muted',
                      'font-weight-normal',
                      'border-bottom-dashed'
                    )}
                  >
                    Income Payout Rate (%)
                  </th>
                </tr>
                <tr>
                  <th {...className('text-left', 'first-column')}>Contribution Dates</th>
                  <th {...className('text-right', 'second-column')}>
                    Single Life with 10-Year Guarantee
                  </th>
                  <th {...className('text-right', 'third-column')}>
                    Joint Life With 20-Year Guarantee
                  </th>
                </tr>
              </thead>
              <tbody {...className('table-border-bottom')}>
                {incomePayoutRates.map((rate, index) => (
                  <tr
                    data-key={`${rate.label || ''}-${index}`}
                    key={`${rate.label || ''}-${index}`}
                  >
                    <td {...className('text-left')}>{format.text(rate.label)}</td>
                    <td {...className('text-right', 'font-weight-bold')}>
                      {format.number(rate.rateSingleLife, {
                        formatString: '0.0'
                      })}
                    </td>
                    <td {...className('text-right', 'font-weight-bold')}>
                      {format.number(rate.rateJointLife, {
                        formatString: '0.0'
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableBasic>
            <span {...className('paragraph-standard')}>
              {
                'Rates shown above are based on a 67-year old choosing a single life annuity with a 10-year guarantee period or a joint life annuity with a 20 year guarantee period.'
              }
            </span>
          </CardModule.Body>

          <CardModule.Footer {...className('text-right')}>
            <ButtonDisclosures module={'income-payout-rates'} />
          </CardModule.Footer>
        </Col>
      </Row>
    </CardModule>
  );
};

IncomePayoutRates.defaultProps = {
  incomePayoutRates: []
};

IncomePayoutRates.propTypes = {
  asOfDate: PropTypes.string,
  incomePayoutRates: PropTypes.arrayOf(PropTypes.shape({})),
  isTIAAQdia: PropTypes.bool,
  extraText: PropTypes.string
};

export default IncomePayoutRates;
